import React, { useContext, useEffect, useReducer } from 'react';

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { FunFactsContent } from './FunFactsContent';

import { VisibleOn } from '../VisibleOn';
import { dynamicTemplate } from '../../services/dynamicTemplate';

import { media } from '../../services/media';
import { themeStyles } from '../../services/themeStyles';
import { useStoreData, useStoryblokStrings } from '../../hooks';
import { ApiContext, CoreContext } from '../../providers';
import { addUserData } from '../../state';

const getFunFact = (
    number: any,
    funFacts: any = null,
    funFactsStrings: any = null,
    myScoreStringsVariables: any = null,
) => {
    if (!funFacts || !funFactsStrings || !myScoreStringsVariables) {
        return '';
    }

    const factVariables = funFacts[number];

    const factKey = factVariables.descr;
    const fact = funFactsStrings[factKey];

    if (!fact) {
        return '';
    }
    let countryKey = factVariables.var && factVariables.var['country'];
    if (countryKey) {
        countryKey = myScoreStringsVariables[countryKey];
    }

    const templateVariables = { ...factVariables.var, country: countryKey };

    if (templateVariables) {
        return dynamicTemplate(fact, templateVariables);
    }

    return fact;
};

const initialStateFunFact = { number: 0, length: 0 };

const funFactReducer = (state: any, action: any) => {
    switch (action.type) {
        case 'nextFact':
            return {
                ...state,
                number: state.length - 1 > state.number ? state.number + 1 : 0,
            };
        case 'changeLength':
            return {
                ...state,
                length: action.value,
            };
        default:
            return state;
    }
};

interface FunFactsProps {
    title: string;
    errorMsg?: string;
}

export const FunFacts: React.FC<FunFactsProps> = ({ title, errorMsg }) => {
    const { Upscore } = useContext(ApiContext);

    const { settings, store } = useContext(CoreContext);
    const { funFacts } = useStoreData().user;
    const funFactsStrings = useStoryblokStrings('fun-facts');
    const myScoreStringsVariables = useStoryblokStrings(
        'my-score-string-variables',
    );

    const [funFactStore, funFactDispatch] = useReducer(
        funFactReducer,
        initialStateFunFact,
    );

    useEffect(() => {
        if (funFacts) return;

        void (async function fetchFunFacts() {
            await Upscore.getFunfacts().then((response: any) => {
                store.dispatch(addUserData('funFacts', response || null));

                funFactDispatch({
                    type: 'changeLength',
                    value: (response && response.length) || 0,
                });
            });
        })();
    }, []);

    useEffect(() => {
        if (!funFacts) return;

        const changeFunFacts = setInterval(
            () => {
                funFactDispatch({
                    type: 'nextFact',
                });
            },
            settings.languageVersion === 'pengestyrke' ? 1500 : 3000,
        );

        return () => clearInterval(changeFunFacts);
    }, [funFacts]);

    const currectFunFact =
        errorMsg ||
        getFunFact(
            funFactStore.number,
            funFacts,
            funFactsStrings,
            myScoreStringsVariables,
        );

    if (!funFacts) return null;

    return (
        <Wrapper>
            <FunFactsContent>
                {!errorMsg && (
                    <VisibleOn tablet desktop>
                        <h2>{title}</h2>
                    </VisibleOn>
                )}
                <p>{currectFunFact}</p>
            </FunFactsContent>
        </Wrapper>
    );
};

const Upscore = themeStyles(
    'upscore',
    css`
        max-width: 28%;
        position: absolute;
        right: 4.5vw;
        bottom: 5.9vw;

        ${media.min('lg')} {
            width: 26%;
        }
    `,
);

const Pengestyrke = themeStyles(
    'pengestyrke',
    css`
        background-color: #eef0eb;
        color: white;

        ${media.min('lg')} {
            width: 100%;
        }
    `,
);

const Wrapper = styled.div`
    ${media.min('md')} {
        display: flex;
        flex-flow: column wrap;
        align-items: center;
        justify-content: flex-end;
        width: 100%;
        min-height: 100%;
        text-align: center;

        ${Upscore}
        ${Pengestyrke}
    }
`;
